/* eslint-disable max-lines */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Badge
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';
import StorageIcon from '@mui/icons-material/Storage';
import ArticleIcon from '@mui/icons-material/Article';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

// eslint-disable-next-line max-lines-per-function
const GlobalTokenPoolBrowser = () => {
  useAuth();
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [healthData, setHealthData] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState(null);
  const [aggregations, setAggregations] = useState(null);

  // Token type edit dialog
  const [editTypeDialogOpen, setEditTypeDialogOpen] = useState(false);
  const [editingToken, setEditingToken] = useState(null);
  const [newTokenType, setNewTokenType] = useState('');
  const [editReason, setEditReason] = useState('');
  const [editingType, setEditingType] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Filters
  const [tokenTypeFilter, setTokenTypeFilter] = useState('');
  const [sortBy, setSortBy] = useState('lastUpdated');
  const [sortOrder, _setSortOrder] = useState('desc');

  // Check authorization on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await api.get('/adminApi/api/admin/verify');
        if (response.isPlatformAdmin) {
          setAuthorized(true);
          await fetchHealthData();
          await performSearch();
        } else {
          setAuthorized(false);
        }
      } catch (error) {
        console.error('Authorization check failed:', error);
        setError('Failed to verify admin access');
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchHealthData = async () => {
    try {
      const health = await api.get('/adminApi/api/admin/es-browser/health');
      setHealthData(health);
    } catch (error) {
      console.error('Failed to fetch health data:', error);
    }
  };

  const performSearch = useCallback(async (resetPage = false) => {
    setSearching(true);
    setError(null);

    if (resetPage) {
      setPage(0);
    }

    try {
      const searchPayload = {
        searchText: searchText.trim(),
        from: resetPage ? 0 : page * pageSize,
        size: pageSize,
        sort: [{ [sortBy]: sortOrder }]
      };

      // Add token type filter if selected
      if (tokenTypeFilter) {
        searchPayload.query = {
          bool: {
            must: [
              searchText.trim() ? {
                multi_match: {
                  query: searchText,
                  fields: ['canonicalText^2', 'originalText', 'entityId'],
                  type: 'phrase_prefix'
                }
              } : { match_all: {} },
              { term: { tokenType: tokenTypeFilter } }
            ].filter(Boolean)
          }
        };
      }

      const [searchResponse, aggResponse] = await Promise.all([
        api.post('/adminApi/api/admin/es-browser/search', searchPayload),
        api.post('/adminApi/api/admin/es-browser/aggregate', {
          aggregations: {
            tokenTypes: {
              terms: { field: 'tokenType.keyword', size: 20 }
            },
            recentModified: {
              date_histogram: {
                field: 'lastUpdated',
                calendar_interval: 'day',
                format: 'yyyy-MM-dd'
              }
            },
            entityCount: {
              cardinality: { field: 'entityId.keyword' }
            }
          }
        })
      ]);

      setSearchResults(searchResponse.hits);
      setTotalResults(searchResponse.total);
      setAggregations(aggResponse.aggregations);
    } catch (error) {
      console.error('Search failed:', error);
      setError('Failed to search documents');
    } finally {
      setSearching(false);
    }
  }, [searchText, page, pageSize, sortBy, sortOrder, tokenTypeFilter]);

  const handleSearch = () => {
    performSearch(true);
  };

  const handleRefresh = async () => {
    await fetchHealthData();
    await performSearch();
  };

  const handleViewDocument = async (doc) => {
    try {
      const fullDoc = await api.get(`/adminApi/api/admin/es-browser/document/${doc.id}`);
      setSelectedDocument(fullDoc);
      setDocumentDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch document:', error);
      setError('Failed to load document details');
    }
  };

  const handleEditTokenType = (token) => {
    setEditingToken(token);
    setNewTokenType(token.tokenType);
    setEditReason('');
    setEditTypeDialogOpen(true);
  };

  const handleSaveTokenType = async () => {
    if (!editReason.trim()) {
      setError('Please provide a reason for changing the token type');
      return;
    }

    setEditingType(true);
    setError(null);

    try {
      await api.put(`/adminApi/api/admin/es-browser/document/${editingToken.id}/type`, {
        tokenType: newTokenType,
        reason: editReason
      });

      // Update the token in the search results
      setSearchResults(prev => prev.map(token =>
        token.id === editingToken.id
          ? { ...token, tokenType: newTokenType }
          : token
      ));

      // Close dialog and reset
      setEditTypeDialogOpen(false);
      setEditingToken(null);
      setNewTokenType('');
      setEditReason('');

      // Show success message
      setError(null);
    } catch (error) {
      console.error('Failed to update token type:', error);
      setError('Failed to update token type: ' + (error.message || 'Unknown error'));
    } finally {
      setEditingType(false);
    }
  };

  const columns = [
    {
      field: 'canonicalText',
      headerName: 'Canonical Text',
      flex: 1.5,
      minWidth: 150,
      renderCell: (params) => (
        <Tooltip title={params.value || ''}>
          <span>{params.value || '-'}</span>
        </Tooltip>
      )
    },
    {
      field: 'originalText',
      headerName: 'Original Text',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Tooltip title={params.value || ''}>
          <span style={{ fontStyle: 'italic', color: '#666' }}>{params.value || '-'}</span>
        </Tooltip>
      )
    },
    {
      field: 'tokenType',
      headerName: 'Type',
      width: 160,  // Increased width to prevent truncation
      renderCell: (params) => (
        <Tooltip title={params.value || 'Unknown'}>
          <Chip
            label={params.value}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ maxWidth: '100%' }}  // Ensure chip doesn't get truncated
          />
        </Tooltip>
      )
    },
    {
      field: 'primaryEntity',
      headerName: 'Primary Entity',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'entityCount',
      headerName: 'Entities',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip label={params.value || 0} size="small" variant="filled" />
      )
    },
    {
      field: 'totalOccurrences',
      headerName: 'Occurrences',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value?.toLocaleString() || 0}
        </Typography>
      )
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 150,
      valueFormatter: (params) => {
        if (!params.value) return '-';
        return new Date(params.value).toLocaleDateString();
      }
    },
    {
      field: 'lastUpdated',
      headerName: 'Last Updated',
      width: 150,
      valueFormatter: (params) => {
        if (!params.value) return '-';
        return new Date(params.value).toLocaleDateString();
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => handleViewDocument(params.row)}>
              <InfoIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Token Type">
            <IconButton size="small" onClick={() => handleEditTokenType(params.row)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!authorized) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Platform Administrator access required to view the Global Token Pool.
      </Alert>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
      {/* Header with Health Status */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Typography variant="h5" gutterBottom>
              Global Token Pool Browser
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Browse and analyze entity tokens across all organizations
            </Typography>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            {healthData && (
              <Grid container spacing={1}>
                <Grid
                  size={{
                    xs: 6,
                    sm: 3
                  }}>
                  <Tooltip title="Cluster Status">
                    <Chip
                      icon={<StorageIcon />}
                      label={healthData.status}
                      color={healthData.status === 'green' ? 'success' : 'warning'}
                      size="small"
                    />
                  </Tooltip>
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    sm: 3
                  }}>
                  <Tooltip title="Total Documents">
                    <Chip
                      icon={<ArticleIcon />}
                      label={`${healthData.documents.toLocaleString()} docs`}
                      variant="outlined"
                      size="small"
                    />
                  </Tooltip>
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    sm: 3
                  }}>
                  <Tooltip title="Storage Used">
                    <Chip
                      label={healthData.storage}
                      variant="outlined"
                      size="small"
                    />
                  </Tooltip>
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    sm: 3
                  }}>
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                  >
                    Refresh
                  </Button>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Paper>
      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
          <Tab label="Search" icon={<SearchIcon />} iconPosition="start" />
          <Tab
            label="Analytics"
            icon={
              <Badge badgeContent={aggregations?.entityCount?.value || 0} color="primary">
                <AnalyticsIcon />
              </Badge>
            }
            iconPosition="start"
          />
        </Tabs>

        {tabValue === 0 && (
          <Box>
            <Grid container spacing={2} alignItems="center">
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <TextField
                  fullWidth
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search by text, entity ID, or token type..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchText && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchText('')}>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 2
                }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Token Type</InputLabel>
                  <Select
                    value={tokenTypeFilter}
                    onChange={(e) => setTokenTypeFilter(e.target.value)}
                    label="Token Type"
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="person">Person</MenuItem>
                    <MenuItem value="organization">Organization</MenuItem>
                    <MenuItem value="location">Location</MenuItem>
                    <MenuItem value="date">Date</MenuItem>
                    <MenuItem value="product">Product</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 2
                }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    label="Sort By"
                  >
                    <MenuItem value="lastUpdated">Last Updated</MenuItem>
                    <MenuItem value="_score">Search Relevance</MenuItem>
                    <MenuItem value="canonicalText.keyword">Canonical Text</MenuItem>
                    <MenuItem value="tokenType">Token Type</MenuItem>
                    {/* Note: Confidence score is calculated during matching, not stored */}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 2
                }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSearch}
                  disabled={searching}
                  startIcon={searching ? <CircularProgress size={20} /> : <SearchIcon />}
                >
                  Search
                </Button>
              </Grid>
            </Grid>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}

        {tabValue === 1 && aggregations && (
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Token Types Distribution
                  </Typography>
                  {aggregations.tokenTypes?.buckets?.map((bucket) => (
                    <Box key={bucket.key} display="flex" justifyContent="space-between" mb={1}>
                      <Chip label={bucket.key} size="small" />
                      <Typography variant="body2">{bucket.doc_count}</Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Unique Entities
                  </Typography>
                  <Typography variant="h3" color="primary">
                    {aggregations.entityCount?.value?.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total unique entities in pool
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Recent Activity
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Documents modified in last 7 days
                  </Typography>
                  {aggregations.recentModified?.buckets?.slice(0, 5).map((bucket) => (
                    <Box key={bucket.key_as_string} display="flex" justifyContent="space-between">
                      <Typography variant="caption">{bucket.key_as_string}</Typography>
                      <Typography variant="caption">{bucket.doc_count}</Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Paper>
      {/* Results Grid */}
      {tabValue === 0 && (
        <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <DataGrid
            rows={searchResults}
            columns={columns}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            rowsPerPageOptions={[10, 25, 50, 100]}
            pagination
            page={page}
            onPageChange={setPage}
            rowCount={totalResults}
            paginationMode="server"
            loading={searching}
            disableSelectionOnClick
            autoHeight={false}
            sx={{
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid rgba(224, 224, 224, 0.4)',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
              }
            }}
          />
          <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              Showing {searchResults.length} of {totalResults.toLocaleString()} total results
            </Typography>
          </Box>
        </Paper>
      )}
      {/* Document Detail Dialog */}
      <Dialog
        open={documentDialogOpen}
        onClose={() => setDocumentDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Document Details
          {selectedDocument && (
            <Typography variant="caption" display="block" color="text.secondary">
              ID: {selectedDocument.id}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedDocument && (
            <Box>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Typography variant="subtitle2" gutterBottom>Canonical Text</Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography>{selectedDocument.canonicalText || '-'}</Typography>
                  </Paper>
                </Grid>
                {selectedDocument.originalText && (
                  <Grid size={12}>
                    <Typography variant="subtitle2" gutterBottom>Original Text</Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography>{selectedDocument.originalText}</Typography>
                    </Paper>
                  </Grid>
                )}
                <Grid size={12}>
                  <Typography variant="subtitle2">Token Type</Typography>
                  <Chip label={selectedDocument.tokenType} color="primary" sx={{ mt: 1 }} />
                </Grid>
                <Grid size={12}>
                  <Typography variant="subtitle2">Entity ID</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 1 }}>
                    {selectedDocument.entityId}
                  </Typography>
                </Grid>
                {selectedDocument.metadata && (
                  <Grid size={12}>
                    <Typography variant="subtitle2" gutterBottom>Metadata</Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                        {JSON.stringify(selectedDocument.metadata, null, 2)}
                      </pre>
                    </Paper>
                  </Grid>
                )}
                <Grid size={6}>
                  <Typography variant="subtitle2">Created</Typography>
                  <Typography variant="body2">
                    {new Date(selectedDocument.createdAt).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="subtitle2">Last Updated</Typography>
                  <Typography variant="body2">
                    {new Date(selectedDocument.lastUpdated).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocumentDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* Token Type Edit Dialog */}
      <Dialog
        open={editTypeDialogOpen}
        onClose={() => !editingType && setEditTypeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Token Type
          {editingToken && (
            <Typography variant="caption" display="block" color="text.secondary">
              Token: {editingToken.canonicalText} (ID: {editingToken.id})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>New Token Type</InputLabel>
              <Select
                value={newTokenType}
                onChange={(e) => setNewTokenType(e.target.value)}
                label="New Token Type"
                disabled={editingType}
              >
                <MenuItem value="PERSON_NAME">PERSON_NAME</MenuItem>
                <MenuItem value="ORGANIZATION_NAME">ORGANIZATION_NAME</MenuItem>
                <MenuItem value="LOCATION">LOCATION</MenuItem>
                <MenuItem value="EMAIL_ADDRESS">EMAIL_ADDRESS</MenuItem>
                <MenuItem value="PHONE_NUMBER">PHONE_NUMBER</MenuItem>
                <MenuItem value="DATE">DATE</MenuItem>
                <MenuItem value="DATE_OF_BIRTH">DATE_OF_BIRTH</MenuItem>
                <MenuItem value="STREET_ADDRESS">STREET_ADDRESS</MenuItem>
                <MenuItem value="CREDIT_CARD_NUMBER">CREDIT_CARD_NUMBER</MenuItem>
                <MenuItem value="US_SOCIAL_SECURITY_NUMBER">US_SOCIAL_SECURITY_NUMBER</MenuItem>
                <MenuItem value="US_DRIVERS_LICENSE_NUMBER">US_DRIVERS_LICENSE_NUMBER</MenuItem>
                <MenuItem value="US_PASSPORT">US_PASSPORT</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Reason for Change (Required)"
              multiline
              rows={3}
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
              disabled={editingType}
              error={!editReason.trim() && editReason !== ''}
              helperText="Please provide a detailed reason for changing the token type. This will be logged in the audit trail."
              required
            />

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditTypeDialogOpen(false)}
            disabled={editingType}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveTokenType}
            variant="contained"
            disabled={editingType || !editReason.trim() || newTokenType === editingToken?.tokenType}
            startIcon={editingType && <CircularProgress size={16} />}
          >
            {editingType ? 'Saving...' : 'Save Change'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GlobalTokenPoolBrowser;